import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Link2, Pause, Play } from "lucide-react";
import { Button, ProgressBar, SegmentedControl, Skeleton, StackedBarCell, StackedBarLegend, StatusDot, cn } from "@fox1ck-org/ui";
import { LineChart } from "@fox1ck-org/ui/charts/recharts";
import { ConversionStatus } from "@gen/tracker/v1/conversion_pb";

import { usePauseToggle } from "@/components/campaign/card-header";
import { CardKpis, cardDelta } from "@/components/campaign/kpis";
import { useCanWrite } from "@/lib/permissions";
import { MetricGrid, type MetricTile } from "@/components/common/metric-grid";
import { Aside, Panel } from "@/components/campaign/panel";
import { useIsPhone } from "@/lib/hooks/use-phone";
import { useReportParams } from "@/lib/hooks/use-report-params";
import { rowValues, useDimensionNames } from "@/components/reports/names";
import { useT } from "@/lib/i18n";
import { DASH, formatInt, formatMoney, formatPct } from "@/lib/format";
import { addDays, dayIn } from "@/lib/period";
import { parseDecimalString } from "@/lib/utils";
import type { Bag } from "@/lib/campaigns-table";
import { statsRowToNumbers } from "@/lib/services/reporting-schema-service";
import { useCampaignReport } from "@/lib/services/campaign-report-service";
import { useCampaignCard } from "@/lib/services/campaign-service";
import { useSearchConversions } from "@/lib/services/conversion-service";
import { useReportTimezone } from "@/lib/services/workspace-service";
import { expensiveAd, holdBuckets, nightRejects, staleHold, weakLander, type Insight, type Row } from "@/lib/campaign-insights";
import { EmptyCampaign } from "./empty-campaign";
import type { CardContext } from "./card";

/**
 * Сводка (доска CSummary): the eight numbers with their change, money by
 * day, the click's path from visit to approve, what became of the leads and
 * the few things that stand out. Every figure is a registry metric from the
 * same reader as the list and the report.
 *
 * A campaign that has never had a visit is not «zero» — it is waiting for its
 * first click (доска CEmpty): the strip shows dashes and the body what is
 * left to do before the link goes into the ads.
 *
 * On a phone (доска MCampaign): six of the numbers, the click's path from
 * people to approve and the way to the link — the same bag of the same reader.
 */
export function SummaryTab({ ctx }: { ctx: CardContext }) {
  const tz = useReportTimezone();
  const phone = useIsPhone();
  const created = (ctx.campaign.createdAt || "").slice(0, 10);
  const today = dayIn(new Date(), tz);
  // Since it was created, not over the Top period: a quiet week is not a new campaign.
  const lifeRange = { from: created || today, to: today, tz };
  const life = useCampaignCard(created ? ctx.campaign.id : undefined, lifeRange, undefined);
  const visits = life.data?.stats ? statsRowToNumbers(life.data.stats).visits : undefined;
  if (life.data && (visits ?? 0) === 0) return <EmptyCampaign ctx={ctx} lifeRange={lifeRange} />;
  if (phone) return <PhoneSummary ctx={ctx} />;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      <CardKpis v={ctx.v} prev={ctx.prev} loading={ctx.loading} />
      <div className="grid shrink-0 grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
        <MoneyByDay campaignId={ctx.campaign.id} />
        <ClickPath v={ctx.v} />
      </div>
      <div className="grid min-h-0 grid-cols-1 gap-3 xl:flex-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.55fr)]">
        <LeadOutcomes ctx={ctx} />
        <StandsOut ctx={ctx} />
      </div>
    </div>
  );
}

function PhoneSummary({ ctx }: { ctx: CardContext }) {
  const t = useT();
  const canWrite = useCanWrite();
  const toggle = usePauseToggle(ctx.campaign);
  const { v, prev: p } = ctx;
  const signed = (n: number | undefined) => (n === undefined || n === 0 ? "neutral" : n > 0 ? "ok" : "danger");
  const tiles: MetricTile[] = [
    { key: "clicks", label: t("cols.clicks"), value: formatInt(v?.clicks), delta: cardDelta("clicks", "count", v?.clicks, p?.clicks) },
    { key: "leads", label: t("cols.leads"), value: formatInt(v?.leads), delta: cardDelta("leads", "count", v?.leads, p?.leads) },
    { key: "approved", label: t("cols.approved"), value: formatInt(v?.conversions), note: t("adapt.approve", { pct: formatPct(v?.approve_rate, { digits: 0 }) }) },
    { key: "cost", label: t("cols.cost"), value: formatMoney(v?.cost), delta: cardDelta("cost", "count", v?.cost, p?.cost) },
    { key: "profit", label: t("cols.profit"), value: formatMoney(v?.profit, { signed: true }), delta: cardDelta("profit", "profit", v?.profit, p?.profit), tone: signed(v?.profit) },
    { key: "roi", label: t("cols.roi"), value: formatPct(v?.roi, { digits: 0, signed: true }), delta: cardDelta("roi", "pp", v?.roi, p?.roi), tone: signed(v?.roi) },
  ];
  const people = v?.clicks ?? 0;
  const steps = v
    ? [
        { key: "people", label: t("card.path.people"), n: v.clicks, note: "" },
        { key: "cta", label: t("card.path.cta"), n: v.cta_clicks, note: t("adapt.path.lpCtr", { pct: formatPct(v.lp_ctr, { digits: 0 }) }) },
        { key: "lead", label: t("card.path.lead"), n: v.leads, note: t("adapt.path.cr", { cr: formatPct(v.cr) }) },
        { key: "approve", label: t("card.path.approve"), n: v.conversions, note: t("card.path.approve.share", { pct: formatPct(v.approve_rate, { digits: 0 }) }) },
      ]
    : [];
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      <MetricGrid tiles={tiles} loading={ctx.loading} label={t("card.tab.summary")} />
      <h2 className="shrink-0 pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("card.path.title")}</h2>
      <section className="flex shrink-0 flex-col gap-2.5 rounded-lg border border-border bg-card px-3.5 py-3" aria-label={t("card.path.title")}>
        {!v ? (
          <Skeleton className="h-36" />
        ) : (
          steps.map((s) => (
            <div key={s.key} className="flex flex-col gap-1">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="min-w-0 truncate">{s.label}</span>
                <span className="whitespace-nowrap">
                  <b className="font-mono font-semibold tabular-nums">{formatInt(s.n)}</b> <span className="text-xs text-muted-foreground">{s.note}</span>
                </span>
              </div>
              <ProgressBar size="md" value={s.n ?? 0} max={people || 1} showPercent={false} />
            </div>
          ))
        )}
      </section>
      <div className="grid shrink-0 auto-cols-fr grid-flow-col gap-2">
        {canWrite && !toggle.archived ? (
          <Button variant="secondary" className="h-11" disabled={toggle.pending || toggle.locked} onClick={toggle.run}>
            {toggle.active ? <Pause className="size-4" strokeWidth={1.5} /> : <Play className="size-4" strokeWidth={1.5} />}
            {t(toggle.active ? "campaign.action.pause" : "campaign.action.activate")}
          </Button>
        ) : null}
        <Button variant="secondary" className="h-11" onClick={() => ctx.setTab("links")}>
          <Link2 className="size-4" strokeWidth={1.5} />
          {t("card.action.link")}
        </Button>
      </div>
    </div>
  );
}

// --- Деньги по дням -----------------------------------------------------------

const SERIES = [
  { key: "revenue", color: "var(--chart-1)", dashed: false },
  { key: "cost", color: "var(--chart-5)", dashed: true },
  { key: "profit", color: "var(--ok)", dashed: false },
] as const;

/** «2026-10-08» → «08.10». Pure. */
export function dayLabel(iso: string): string {
  return /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : iso;
}

function MoneyByDay({ campaignId }: { campaignId: string }) {
  const t = useT();
  const p = useReportParams();
  const [by, setBy] = useState<"day" | "hour">("day");
  const q = useCampaignReport({ campaignId, from: p.from, to: p.to, tz: p.tz, basis: p.basis, dims: [by] });
  const data = useMemo(() => {
    const rows = (q.data?.rows ?? [])
      .filter((r) => !r.spendWithoutClicks && r.dimensionPath.length > 0)
      .map((r) => {
        const v = statsRowToNumbers(r.stats);
        const key = r.dimensionPath[0] ?? "";
        return { key, label: by === "day" ? dayLabel(key) : key.padStart(2, "0"), revenue: v.revenue ?? 0, cost: v.cost ?? 0, profit: v.profit ?? 0 };
      });
    return rows.sort((a, b) => (by === "hour" ? Number(a.key) - Number(b.key) : a.key.localeCompare(b.key)));
  }, [q.data, by]);

  return (
    <Panel
      title={t("card.money.title")}
      aside={
        <div className="flex items-center gap-3">
          {SERIES.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <i aria-hidden className="inline-block size-2.5 rounded-sm" style={{ background: s.color }} />
              {t(`card.money.${s.key}`)}
            </span>
          ))}
          <span aria-hidden className="h-5 w-px bg-border" />
          <SegmentedControl
            size="sm"
            aria-label={t("card.money.by")}
            value={by}
            onValueChange={(v: string) => setBy(v === "hour" ? "hour" : "day")}
            options={[
              { value: "day", label: t("card.money.day") },
              { value: "hour", label: t("card.money.hour") },
            ]}
          />
        </div>
      }
    >
      <div className="h-52 px-2 pt-1">
        {q.isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : data.length === 0 ? (
          <div className="grid h-full place-items-center text-sm text-muted-foreground">{t("card.money.empty")}</div>
        ) : (
          <LineChart
            data={data}
            xKey="label"
            variant="area"
            height={204}
            showLegend={false}
            animate={false}
            valueFormatter={(v) => formatMoney(v)}
            lines={SERIES.map((s) => ({ key: s.key, label: t(`card.money.${s.key}`), color: s.color, dashed: s.dashed }))}
          />
        )}
      </div>
    </Panel>
  );
}

// --- Путь клика ------------------------------------------------------------------

export interface Step {
  key: string;
  label: string;
  value: number | undefined;
  note: string;
  tone?: "danger" | "ok";
}

/**
 * The click's path (доска CSummary): visits, the bots the class cut, people
 * (= «Клики»), lander shows, button clicks, leads, approves — each with the
 * line that explains it, its bar against all visits. Pure over the bag.
 */
export function clickPathSteps(v: Bag, t: ReturnType<typeof useT>): Step[] {
  const ratio = (a: number | undefined, b: number | undefined) => (a !== undefined && b ? a / b : undefined);
  return [
    { key: "visits", label: t("card.path.visits"), value: v.visits, note: t("card.path.visits.note") },
    { key: "bots", label: t("card.path.bots"), value: v.bots, note: t("card.path.bots.share", { pct: formatPct(ratio(v.bots, v.visits)) }), tone: "danger" },
    { key: "people", label: t("card.path.people"), value: v.clicks, note: t("card.path.people.note") },
    { key: "shows", label: t("card.path.shows"), value: v.lander_shows, note: t("card.path.shows.note", { pct: formatPct(ratio(v.lander_shows, v.clicks), { digits: 0 }) }) },
    { key: "cta", label: t("card.path.cta"), value: v.cta_clicks, note: t("card.path.cta.share", { pct: formatPct(v.lp_ctr, { digits: 0 }) }) },
    { key: "lead", label: t("card.path.lead"), value: v.leads, note: t("card.path.lead.share", { cr: formatPct(v.cr) }) },
    { key: "approve", label: t("card.path.approve"), value: v.conversions, note: t("card.path.approve.share", { pct: formatPct(v.approve_rate, { digits: 0 }) }), tone: "ok" },
  ];
}

function ClickPath({ v }: { v: Bag | undefined }) {
  const t = useT();
  const steps = v ? clickPathSteps(v, t) : [];
  const max = v?.visits ?? 0;
  return (
    <Panel title={t("card.path.title")} aside={<Aside>{t("card.path.aside")}</Aside>}>
      {!v ? (
        <Skeleton className="m-3.5 h-55" />
      ) : (
        <ul className="flex flex-col py-1.5">
          {steps.map((s) => (
            <li key={s.key} className="grid grid-cols-[--spacing(28)_minmax(0,1fr)_--spacing(16)] items-center gap-2.5 px-3.5 py-0.5 text-sm">
              <span className={cn("truncate", s.tone === "danger" && "text-muted-foreground")}>{s.label}</span>
              <span className="flex min-w-0 flex-col gap-0.5">
                <ProgressBar size="md" value={s.value ?? 0} max={max || 1} tone={s.tone} showPercent={false} />
                <span className="truncate text-xs text-muted-foreground" title={s.note}>
                  {s.note}
                </span>
              </span>
              <span className="text-right font-mono tabular-nums">
                {s.key === "bots" && s.value ? "−" : ""}
                {formatInt(s.value)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// --- Исходы лидов -------------------------------------------------------------

const HOLD_PAGE = 1000;

/** The campaign's leads in hold over the period, for their age. */
function useHold(ctx: CardContext) {
  const p = useReportParams();
  return useSearchConversions({
    range: { from: p.from, to: p.to, timezone: p.tz },
    campaignId: ctx.campaign.id,
    status: ConversionStatus.HOLD,
    limit: HOLD_PAGE,
  });
}

/** Seconds each lead has waited, from its arrival. Pure. */
export function holdAges(items: { leadTs: string; ts: string }[], now = Date.now()): number[] {
  return items.map((c) => {
    const at = Date.parse(c.leadTs || c.ts);
    return Number.isFinite(at) ? Math.max(0, (now - at) / 1000) : 0;
  });
}

function LeadOutcomes({ ctx }: { ctx: CardContext }) {
  const t = useT();
  const v = ctx.v;
  const hold = useHold(ctx);
  const [fresh, mid, old] = holdBuckets(holdAges(hold.data?.items ?? []));
  const rows: { tone: "ok" | "neutral" | "danger" | "warn"; label: string; n: number | undefined; note: string; money: string }[] = v
    ? [
        { tone: "ok", label: t("card.outcome.approved"), n: v.conversions, note: t("card.outcome.approved.why"), money: v.revenue ? formatMoney(v.revenue, { signed: true }) : DASH },
        { tone: "neutral", label: t("card.outcome.hold"), n: v.conversions_hold, note: t("card.outcome.hold.why"), money: v.revenue_in_transit !== undefined ? `≈ ${formatMoney(v.revenue_in_transit)}` : DASH },
        { tone: "danger", label: t("card.outcome.rejected"), n: v.rejected, note: t("card.outcome.rejected.why"), money: DASH },
        { tone: "warn", label: t("card.outcome.trash"), n: v.trash, note: t("card.outcome.trash.why"), money: DASH },
      ]
    : [];
  const aging = [
    { tone: "neutral" as const, label: t("card.outcome.age.fresh"), value: fresh },
    { tone: "warn" as const, label: t("card.outcome.age.mid"), value: mid },
    { tone: "danger" as const, label: t("card.outcome.age.old"), value: old },
  ];
  return (
    <Panel title={t("card.outcome.title")} aside={<Aside>{t("card.outcome.aside", { n: formatInt(v?.leads) })}</Aside>}>
      {!v ? (
        <Skeleton className="m-3.5 h-45" />
      ) : (
        <>
          <ul className="flex flex-col">
            {rows.map((r) => (
              <li key={r.label} className="grid grid-cols-[--spacing(3)_--spacing(18)_--spacing(10)_minmax(0,1fr)_auto] items-center gap-2 border-b border-border px-3.5 py-2 text-sm">
                <StatusDot tone={r.tone} />
                <span className="font-medium">{r.label}</span>
                <span className="text-right font-mono tabular-nums">{formatInt(r.n)}</span>
                <span className="truncate pl-2 text-xs text-muted-foreground">{r.note}</span>
                <span className={cn("text-right font-mono text-sm tabular-nums", r.tone === "ok" ? "text-ok" : "text-muted-foreground")}>{r.money}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-2 px-3.5 py-2.5">
            <span className="text-xs text-muted-foreground">{t("card.outcome.wait")}</span>
            {hold.isLoading ? (
              <Skeleton className="h-6" />
            ) : (
              <>
                <StackedBarCell segments={aging} placeholder={t("card.outcome.wait.none")} />
                <StackedBarLegend items={aging.map((a) => ({ tone: a.tone, label: a.label, value: formatInt(a.value) }))} />
              </>
            )}
          </div>
        </>
      )}
    </Panel>
  );
}

// --- Что выделяется ------------------------------------------------------------

function StandsOut({ ctx }: { ctx: CardContext }) {
  const t = useT();
  const p = useReportParams();
  const base = { campaignId: ctx.campaign.id, from: p.from, to: p.to, tz: p.tz, basis: p.basis };
  const ads = useCampaignReport({ ...base, dims: ["creative"] });
  const landers = useCampaignReport({ ...base, dims: ["landing"] });
  const hours = useCampaignReport({ ...base, dims: ["hour"] });
  const hold = useHold(ctx);
  const { name } = useDimensionNames(rowValues(["landing"], landers.data?.rows ?? []));
  const network = ctx.item?.offers[0]?.name ?? "";

  const insights = useMemo(() => {
    const rows = (rs: typeof ads.data, dim: string): Row[] =>
      (rs?.rows ?? [])
        .filter((r) => !r.spendWithoutClicks)
        .map((r) => {
          const key = r.dimensionPath[0] ?? "";
          return { key, label: name(dim, key), v: statsRowToNumbers(r.stats) };
        });
    const items = hold.data?.items ?? [];
    const out: Insight[] = [];
    const a = ctx.v ? expensiveAd(rows(ads.data, "creative"), ctx.v) : undefined;
    const l = weakLander(rows(landers.data, "landing"));
    const n = nightRejects(rows(hours.data, "hour"));
    const h = staleHold(
      holdAges(items),
      items.map((c) => parseDecimalString(c.reportingPayoutUsd)),
      network,
    );
    for (const i of [a, l, n, h]) if (i) out.push(i);
    return out;
  }, [ads.data, landers.data, hours.data, hold.data, ctx.v, name, network]);

  // Each line opens the slice it is about: the report on the right
  // dimension, or the conversions in hold.
  const target: Record<Insight["kind"], { label: string; to: string }> = {
    expensive_ad: { label: t("card.insight.openReport"), to: withQuery(ctx.href("report"), { dims: "creative,landing" }) },
    weak_lander: { label: t("card.insight.openReport"), to: withQuery(ctx.href("report"), { dims: "landing" }) },
    night_rejects: { label: t("card.insight.openReport"), to: withQuery(ctx.href("report"), { dims: "hour" }) },
    stale_hold: { label: t("card.insight.show"), to: withQuery(ctx.href("conversions"), { status: "hold" }) },
  };
  const loading = ads.isLoading || landers.isLoading || hours.isLoading;
  const compare = p.compareRange ? t("card.insight.aside.compare", { days: daysBetween(p.compareRange.from, p.compareRange.to) }) : t("card.insight.aside");

  return (
    <Panel title={t("card.insight.title")} aside={<Aside>{compare}</Aside>}>
      {loading ? (
        <Skeleton className="m-3.5 h-45" />
      ) : insights.length === 0 ? (
        <p className="px-3.5 py-6 text-sm text-muted-foreground">{t("card.insight.none")}</p>
      ) : (
        <ul className="flex flex-col">
          {insights.map((i) => (
            <li key={i.kind} className="flex items-start gap-3 border-b border-border px-3.5 py-2.5 last:border-b-0">
              <StatusDot tone={i.tone} className="mt-1.5" />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm">{t(`card.insight.${i.kind}.title`, i.vars)}</span>
                <span className="truncate text-xs text-muted-foreground">{t(`card.insight.${i.kind}.body`, i.vars)}</span>
              </div>
              <Button asChild variant="ghost" size="sm">
                <Link to={target[i.kind].to}>{target[i.kind].label}</Link>
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** A tab's address with extra query keys of its own. Pure. */
export function withQuery(href: string, extra: Record<string, string>): string {
  const [path, query = ""] = href.split("?");
  const sp = new URLSearchParams(query);
  for (const [k, v] of Object.entries(extra)) sp.set(k, v);
  return `${path}?${sp.toString()}`;
}

function daysBetween(from: string, to: string): number {
  let n = 1;
  for (let d = from; d < to && n < 400; d = addDays(d, 1)) n++;
  return n;
}
